import React, { useEffect, useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import { useIntl } from "react-intl";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getAlertThemeData, getMuiTheme } from "../../../utils/utils";
import palette from "../../../../config/colorPalette";
import { ErrorText } from "../styled";
import { postAlertConfiguration } from "../../../api";
import { setConfigDataAction } from "../../../redux/configSlice";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const AlertConfig = ({ open, setOpen, configData, selectedAsset, selectedEquipment, equipmentTypes }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);
  const [listCategory, setListCategory] = useState(["measurand", "module", "sensor", "external_service"]);
  const [data, setData] = useState(null);
  const [alertName, setAlertName] = useState("");
  const [alertCategory, setAlertCategory] = useState("measurand");
  const [alertType, setAlertType] = useState("");
  const [alertSubType, setAlertSubType] = useState(null);
  const [alertExtraInfo, setAlertExtraInfo] = useState(null);
  const [alertSeverities, setAlertSeverities] = useState([
    {
      type: "info",
      value: 1,
      icon: true,
    },
    {
      type: "warning",
      value: 2,
      icon: true,
    },
    {
      type: "critical",
      value: 3,
      icon: true,
    },
  ]);
  const [alertSeverityDefiner, setAlertSeverityDefiner] = useState("threshold");
  const [alertSeverityValues, setAlertSeverityValues] = useState([
    { value: 1, field: "partial", severity: 1 },
    { value: 2, field: "exposed", severity: 2 },
  ]);
  const [extraSeverities, setExtraSeverities] = useState(null);
  const [errors, setErrors] = useState([]);
  useEffect(() => {
    if (alertName.length < 3) {
      setErrors((prevErrors) => ({ ...prevErrors, "alert-name": "3.characters" }));
    } else {
      setErrors((prevErrors) => {
        const { "alert-name": alertName, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  }, [alertName, setErrors]);

  useMemo(() => {
    let finalData = {};
    let newList = clone(listCategory);
    for (let indexCategory = 0; indexCategory < listCategory.length; indexCategory++) {
      if (listCategory[indexCategory] === "measurand") {
        const equipmentData = equipmentTypes.find((et) => et.name === selectedEquipment.equipmentType);
        finalData[listCategory[indexCategory]] = equipmentData.measurands;
        setAlertType(equipmentData.measurands[0]);
      }
      if (
        listCategory[indexCategory] === "module" &&
        !isNil(selectedAsset.config) &&
        !isNil(selectedAsset.config.modules) &&
        !isEmpty(selectedAsset.config.modules)
      ) {
        if (!isNil(selectedAsset.config)) {
          for (let indexModule = 0; indexModule < selectedAsset.config.modules.length; indexModule++) {
            const moduleData = selectedAsset.config.modules[indexModule];
            if (isNil(finalData[listCategory[indexCategory]])) finalData[listCategory[indexCategory]] = {};
            const packageList = [];
            for (let indexPackage = 0; indexPackage < moduleData.packages.length; indexPackage++) {
              if (!moduleData.packages[indexPackage].measurand) {
                const extraInfo = [];
                const types = ["computed"];
                if (!isNil(moduleData.packages[indexPackage].extraVars)) {
                  for (let indexType = 0; indexType < types.length; indexType++) {
                    for (
                      let indexExtra = 0;
                      indexExtra < moduleData.packages[indexPackage].extraVars[types[indexType]].length;
                      indexExtra++
                    ) {
                      extraInfo.push({
                        extraVar: moduleData.packages[indexPackage].extraVars[types[indexType]][indexExtra].name,
                        type: types[indexType],
                        values: moduleData.packages[indexPackage].extraVars[types[indexType]][indexExtra].values,
                        typeAlertConfig: moduleData.packages[indexPackage].extraVars[types[indexType]][indexExtra].type,
                      });
                    }
                  }
                }
                packageList.push({
                  name: moduleData.packages[indexPackage].name,
                  extraInfo: !isEmpty(extraInfo) ? extraInfo : null,
                });
              }
            }
            finalData[listCategory[indexCategory]][moduleData.name] = packageList;
          }
        }
      } else if (listCategory[indexCategory] === "module") {
        newList = newList.filter(function (item) {
          return item !== "module";
        });
      }
      if (listCategory[indexCategory] === "sensor") {
        if (!isNil(selectedAsset.config) && !isNil(selectedAsset.config.additionalSensors)) {
          const measurands = selectedAsset.config.additionalSensors.measurands;

          if (!finalData[listCategory[indexCategory]]) {
            finalData[listCategory[indexCategory]] = {};
          }

          for (let indexSensor = 0; indexSensor < measurands.length; indexSensor++) {
            const sensorData = measurands[indexSensor];
            const { category, reference } = sensorData;

            if (!finalData[listCategory[indexCategory]][category]) {
              finalData[listCategory[indexCategory]][category] = [];
            }

            finalData[listCategory[indexCategory]][category].push(reference);
          }
        } else if (listCategory[indexCategory] === "sensor") {
          newList = newList.filter(function (item) {
            return item !== "sensor";
          });
        }
      }

      if (listCategory[indexCategory] === "external_service") {
        if (!isNil(selectedAsset.config) && !isNil(selectedAsset.config.externalServices)) {
          finalData[listCategory[indexCategory]] = [];
          for (let indexService = 0; indexService < selectedAsset.config.externalServices.length; indexService++) {
            const serviceData = selectedAsset.config.externalServices[indexService];
            finalData[listCategory[indexCategory]].push(serviceData.name);
          }
        } else {
          newList = newList.filter(function (item) {
            return item !== "external_service";
          });
        }
      }
    }
    setListCategory(newList);
    setData(finalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset]);

  const subIndex = useMemo(() => {
    if (!isNil(alertSubType)) {
      return data[alertCategory][alertType].findIndex((sub) => sub.name === alertSubType || sub === alertSubType);
    } else {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertSubType]);
  const [alertActive, setAlertActive] = useState(true);
  const [alertSeverity, setAlertSeverity] = useState(1);
  const [alertThresholdTypes, setAlertThresholdTypes] = useState([]);
  const [alertTresholdConditions, setAlertTresholdConditions] = useState([]);
  const [alertThresholdValues, setAlertThresholdValues] = useState([]);
  const [alertThresholdSeverities, setAlertThresholdSeverities] = useState([]);
  const handleClose = (setOpen) => {
    setOpen(false);
  };
  const disabled = useMemo(() => !isEmpty(errors), [errors]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog maxWidth="lg" open={open} onClose={() => handleClose(setOpen)}>
        <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
          {`${intl.formatMessage({ id: "alert.config" })}`}
        </DialogTitle>
        <DialogContent style={{ paddingBottom: "0" }}>
          <form autoComplete="off">
            <div>
              <Divider style={{ marginBottom: "1rem", color: palette.primary }} />
              <div style={{ display: "flex" }}>
                <FormControl>
                  <TextField
                    autoFocus
                    value={alertName}
                    variant="outlined"
                    autoComplete="off"
                    margin="dense"
                    id="alert-name"
                    label={intl.formatMessage({ id: "name" })}
                    type="text"
                    fullWidth
                    required
                    error={errors["alert-name"] ? true : false}
                    onChange={(e) => {
                      setAlertName(e.target.value);
                    }}
                  ></TextField>
                  {!isNil(errors["alert-name"]) && (
                    <ErrorText>{intl.formatMessage({ id: errors["alert-name"] })}</ErrorText>
                  )}
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid item>
                      <TextField
                        id="alertCategory"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertCategory}
                        onChange={(e) => {
                          setAlertCategory(e.target.value);
                          setAlertThresholdTypes([]);
                          setAlertTresholdConditions([]);
                          setAlertThresholdValues([]);
                          setAlertThresholdSeverities([]);
                          if (Array.isArray(data[e.target.value])) {
                            setAlertType(data[e.target.value][0]);
                            setAlertSubType(null);
                            setAlertSeverities([
                              {
                                type: "info",
                                value: 1,
                                icon: true,
                              },
                              {
                                type: "warning",
                                value: 2,
                                icon: true,
                              },
                              {
                                type: "critical",
                                value: 3,
                                icon: true,
                              },
                            ]);
                            setAlertSeverityDefiner("threshold");
                          } else {
                            const list = Object.keys(data[e.target.value]);
                            setAlertType(list[0]);
                            const subName = isNil(data[e.target.value][list[0]][0].name)
                              ? data[e.target.value][list[0]][0]
                              : data[e.target.value][list[0]][0].name;
                            setAlertSubType(subName);
                            const auxsubIndex = data[e.target.value][list[0]].findIndex(
                              (sub) => sub.name === subName || sub === subName,
                            );
                            if (!isNil(data[e.target.value][list[0]][auxsubIndex].extraInfo)) {
                              setAlertExtraInfo(data[e.target.value][list[0]][auxsubIndex].extraInfo[0]);
                              const newExtra = data[e.target.value][list[0]][auxsubIndex].extraInfo[0];
                              setAlertSeverityDefiner(newExtra.typeAlertConfig);
                              if (!isNil(newExtra.values)) {
                                const newSeverities = [];
                                for (let indexValue = 0; indexValue < newExtra.values.length; indexValue++) {
                                  newSeverities.push({
                                    type: newExtra.values[indexValue].ref,
                                    value: newExtra.values[indexValue].value,
                                    icon: false,
                                  });
                                }
                                setExtraSeverities(newSeverities);
                                setAlertSeverity(1);
                              } else {
                                setExtraSeverities(null);
                              }
                            } else {
                              setAlertExtraInfo(null);
                              setExtraSeverities(null);
                              setAlertSeverityDefiner("threshold");
                            }
                          }
                        }}
                        select
                        label={intl.formatMessage({ id: "alert.category" })}
                        required
                      >
                        {listCategory.map((cat) => (
                          <MenuItem key={`category_${cat}`} value={cat}>
                            {intl.formatMessage({ id: cat })}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item>
                      <TextField
                        id="alertType"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertType}
                        onChange={(e) => {
                          setAlertType(e.target.value);
                          if (!Array.isArray(data[alertCategory])) {
                            const subName = isNil(data[alertCategory][e.target.value][0].name)
                              ? data[alertCategory][e.target.value][0]
                              : data[alertCategory][e.target.value][0].name;
                            setAlertSubType(subName);
                            const auxsubIndex = data[alertCategory][e.target.value].findIndex(
                              (sub) => sub.name === subName || sub === subName,
                            );
                            if (!isNil(data[alertCategory][e.target.value][auxsubIndex].extraInfo)) {
                              setAlertExtraInfo(data[alertCategory][e.target.value][auxsubIndex].extraInfo[0]);
                              const newExtra = data[alertCategory][e.target.value][auxsubIndex].extraInfo[0];
                              setAlertSeverityDefiner(newExtra.typeAlertConfig);
                              if (!isNil(newExtra.values)) {
                                const newSeverities = [];
                                for (let indexValue = 0; indexValue < newExtra.values.length; indexValue++) {
                                  newSeverities.push({
                                    type: newExtra.values[indexValue].ref,
                                    value: newExtra.values[indexValue].value,
                                    icon: false,
                                  });
                                }
                                setExtraSeverities(newSeverities);
                                setAlertSeverity(1);
                              } else {
                                setExtraSeverities(null);
                              }
                            } else {
                              setAlertExtraInfo(null);
                              setExtraSeverities(null);
                              setAlertSeverityDefiner("threshold");
                            }
                          }
                        }}
                        select
                        label={intl.formatMessage({ id: "alert.type" })}
                        required
                      >
                        {!isNil(data) && Array.isArray(data[alertCategory])
                          ? data[alertCategory].map((type) => (
                              <MenuItem key={`ref_${type}`} value={type}>
                                {intl.formatMessage({ id: type })}
                              </MenuItem>
                            ))
                          : !isNil(data) &&
                            Object.keys(data[alertCategory]).map((type) => (
                              <MenuItem key={`ref_${type}`} value={type}>
                                {intl.formatMessage({ id: type })}
                              </MenuItem>
                            ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    {!isNil(data) && !Array.isArray(data[alertCategory]) && (
                      <Grid item>
                        <TextField
                          id="subAlertType"
                          margin="dense"
                          sx={{ width: "10rem" }}
                          variant="outlined"
                          value={alertSubType}
                          onChange={(e) => {
                            setAlertSubType(e.target.value);
                            const auxsubIndex = data[alertCategory][alertType].findIndex(
                              (sub) => sub.name === e.target.value || sub === e.target.value,
                            );
                            if (!isNil(data[alertCategory][alertType][auxsubIndex].extraInfo)) {
                              setAlertExtraInfo(data[alertCategory][alertType][auxsubIndex].extraInfo[0]);
                              const newExtra = data[alertCategory][alertType][auxsubIndex].extraInfo[0];
                              setAlertSeverityDefiner(newExtra.typeAlertConfig);
                              if (!isNil(newExtra.values)) {
                                const newSeverities = [];
                                for (let indexValue = 0; indexValue < newExtra.values.length; indexValue++) {
                                  newSeverities.push({
                                    type: newExtra.values[indexValue].ref,
                                    value: newExtra.values[indexValue].value,
                                    icon: false,
                                  });
                                }
                                setExtraSeverities(newSeverities);
                                setAlertSeverity(1);
                              } else {
                                setExtraSeverities(null);
                              }
                            } else {
                              setAlertExtraInfo(null);
                              setExtraSeverities(null);
                              setAlertSeverityDefiner("threshold");
                            }
                          }}
                          select
                          label={intl.formatMessage({ id: "alert.subtype" })}
                          required
                        >
                          {!isNil(data) &&
                            !Array.isArray(data[alertCategory]) &&
                            data[alertCategory][alertType].map((subtype) => {
                              const subName = isNil(subtype.name) ? subtype : subtype.name;
                              return (
                                <MenuItem key={`sub_${subtype}`} value={subName}>
                                  {intl.formatMessage({ id: subName })}
                                </MenuItem>
                              );
                            })}
                        </TextField>
                      </Grid>
                    )}
                    {!isNil(data) &&
                      !isNil(data[alertCategory]) &&
                      !isNil(data[alertCategory][alertType]) &&
                      !isNil(data[alertCategory][alertType][subIndex]?.extraInfo) && (
                        <Grid item>
                          <TextField
                            id="subAlertType"
                            margin="dense"
                            sx={{ width: "10rem" }}
                            variant="outlined"
                            value={alertExtraInfo}
                            onChange={(e) => {
                              setAlertExtraInfo(e.target.value);
                              const newExtra = e.target.value;
                              setAlertSeverityDefiner(newExtra.typeAlertConfig);
                              if (!isNil(newExtra.values)) {
                                const newSeverities = [];
                                for (let indexValue = 0; indexValue < newExtra.values.length; indexValue++) {
                                  newSeverities.push({
                                    type: newExtra.values[indexValue].ref,
                                    value: newExtra.values[indexValue].value,
                                    icon: false,
                                  });
                                }
                                setExtraSeverities(newSeverities);
                                setAlertSeverity(1);
                              } else {
                                setExtraSeverities(null);
                              }
                            }}
                            select
                            label={intl.formatMessage({ id: "extra.info" })}
                            required
                          >
                            {!isNil(data) &&
                              !Array.isArray(data[alertCategory]) &&
                              data[alertCategory][alertType][subIndex].extraInfo.map((extraInfo) => (
                                <MenuItem key={`sub_${extraInfo.extraVar}`} value={extraInfo}>
                                  {intl.formatMessage({ id: extraInfo.extraVar })}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Grid>
                      )}
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id={"checkbox_active"}
                            sx={{
                              color: palette.primary,
                              "&.Mui-checked": {
                                color: palette.primary,
                              },
                              marginLeft: "15%",
                              marginTop: "0.5rem",
                            }}
                            defaultChecked={alertActive}
                            onChange={(e) => {
                              setAlertActive(e.target.checked);
                            }}
                          />
                        }
                        label={intl.formatMessage({ id: "active" })}
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={2}>
                    {!isNil(extraSeverities) &&
                      alertSeverityDefiner === "values" &&
                      extraSeverities.map((severity, sevIndex) => (
                        <Grid item key={severity.type}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                                minWidth: "80px",
                                display: "inline-block",
                              }}
                            >
                              {intl.formatMessage({ id: severity.type })}
                            </span>
                            <TextField
                              id="alertSeverity"
                              margin="dense"
                              sx={{ width: "10rem" }}
                              variant="outlined"
                              value={alertSeverityValues[sevIndex].severity}
                              onChange={(e) => {
                                let newSevValues = clone(alertSeverityValues);
                                newSevValues[sevIndex].severity = e.target.value;
                                setAlertSeverityValues(newSevValues);
                              }}
                              select
                              label={intl.formatMessage({ id: "alert.severity" })}
                              required
                            >
                              {alertSeverities.map((severity) => (
                                <MenuItem key={`severity_${severity.type}`} value={severity.value}>
                                  {severity.icon &&
                                    (() => {
                                      const alertData = getAlertThemeData(severity.value, "small", intl);
                                      return (
                                        <Badge
                                          sx={{
                                            backgroundColor: alertData.backgroundColor,
                                            justifyContent: "center",
                                            borderRadius: "16px",
                                            padding: "5px",
                                          }}
                                        >
                                          {alertData.icon}
                                        </Badge>
                                      );
                                    })()}
                                  {intl.formatMessage({ id: severity.type })}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>

                  {alertSeverityDefiner === "threshold" && (
                    <>
                      <h2 style={{ marginTop: "5%" }}>{intl.formatMessage({ id: "thresholds" })}</h2>
                      <Button
                        variant="contained"
                        style={{ backgroundColor: palette.primary, color: "white", width: "6rem", marginTop: "1%" }}
                        onClick={() => {
                          setAlertThresholdTypes([...alertThresholdTypes, "normal"]);
                          setAlertTresholdConditions([...alertTresholdConditions, ">"]);
                          setAlertThresholdValues([...alertThresholdValues, 0]);
                          setAlertThresholdSeverities([...alertThresholdSeverities, 1]);
                        }}
                      >
                        {intl.formatMessage({ id: "add.threshold" })}
                      </Button>
                      {!isEmpty(alertThresholdTypes) &&
                        alertThresholdTypes.map((at, atIndex) => {
                          return (
                            <Grid
                              spacing={1}
                              container
                              direction="row"
                              justifyContent="center"
                              sx={{ marginTop: "1%" }}
                              alignItems="center" // ← cambiado de "flex-start"
                            >
                              <Grid item>
                                <TextField
                                  id={`alertThresholdTypes${atIndex}`}
                                  margin="dense"
                                  sx={{ width: "10rem" }}
                                  variant="outlined"
                                  value={at}
                                  onChange={(e) => {
                                    let newTypes = clone(alertThresholdTypes);
                                    newTypes[atIndex] = e.target.value;
                                    setAlertThresholdTypes(newTypes);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "threshold.type" })}
                                  required
                                >
                                  <MenuItem key={`threhsold_type_normal`} value={"normal"}>
                                    {intl.formatMessage({ id: "normal" })}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_type_average`} value={"average"}>
                                    {intl.formatMessage({ id: "average" })}
                                  </MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item>
                                <TextField
                                  id={`alertThresholdCondition${atIndex}`}
                                  margin="dense"
                                  sx={{ width: "10rem" }}
                                  variant="outlined"
                                  value={alertTresholdConditions[atIndex]}
                                  onChange={(e) => {
                                    let newConditions = clone(alertTresholdConditions);
                                    newConditions[atIndex] = e.target.value;
                                    setAlertTresholdConditions(newConditions);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "threshold.condition" })}
                                  required
                                >
                                  <MenuItem key={`threhsold_condition_>`} value={">"}>
                                    {">"}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_>=`} value={">="}>
                                    {"≥"}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_==`} value={"=="}>
                                    {"=="}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_<=`} value={"<="}>
                                    {"≤"}
                                  </MenuItem>
                                  <MenuItem key={`threhsold_condition_<`} value={"<"}>
                                    {"<"}
                                  </MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item>
                                <TextField
                                  value={alertThresholdValues[atIndex]}
                                  variant="outlined"
                                  autoComplete="off"
                                  margin="dense"
                                  id={`alertThresholdValue_${atIndex}`}
                                  label={intl.formatMessage({ id: "value" })}
                                  type="number"
                                  sx={{ width: "10rem" }} // ← reemplaza fullWidth por ancho fijo
                                  required
                                  // ← eliminado style={{ height: "70%" }}
                                  error={errors[`alertThresholdValue_${atIndex}`] ? true : false}
                                  onChange={(e) => {
                                    const newErrors = { ...errors };
                                    if (isEmpty(e.target.value)) {
                                      newErrors[e.target.id] = "not.empty";
                                    } else {
                                      delete newErrors[e.target.id];
                                    }
                                    setErrors(newErrors);
                                    let newValues = clone(alertThresholdValues);
                                    newValues[atIndex] = parseFloat(e.target.value);
                                    setAlertThresholdValues(newValues);
                                  }}
                                />
                              </Grid>
                              <Grid item>
                                <TextField
                                  id="alertSeverity_threhsold"
                                  margin="dense"
                                  sx={{ width: "10rem" }}
                                  variant="outlined"
                                  value={alertThresholdSeverities[atIndex]}
                                  onChange={(e) => {
                                    const newErrors = { ...errors };
                                    if (isEmpty(e.target.value)) {
                                      newErrors[e.target.id] = "not.empty";
                                    } else {
                                      delete newErrors[e.target.id];
                                    }
                                    setErrors(newErrors);
                                    let newValues = clone(alertThresholdSeverities);
                                    newValues[atIndex] = e.target.value;
                                    setAlertThresholdSeverities(newValues);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "alert.severity" })}
                                  required
                                >
                                  {alertSeverities.map((severity) => (
                                    <MenuItem key={`severity_${severity.type}`} value={severity.value}>
                                      {severity.icon &&
                                        (() => {
                                          const alertData = getAlertThemeData(severity.value, "small", intl);
                                          return (
                                            <Badge
                                              sx={{
                                                backgroundColor: alertData.backgroundColor,
                                                justifyContent: "center",
                                                borderRadius: "16px",
                                                padding: "5px",
                                              }}
                                            >
                                              {alertData.icon}
                                            </Badge>
                                          );
                                        })()}
                                      {intl.formatMessage({ id: severity.type })}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item>
                                <IconButton
                                  style={{ backgroundColor: palette.dividerLight }} // ← eliminado marginTop: "50%"
                                  onClick={() => {
                                    let newTypes = clone(alertThresholdTypes);
                                    newTypes.splice(atIndex, 1);
                                    setAlertThresholdTypes(newTypes);
                                    let newConditions = clone(alertTresholdConditions);
                                    newConditions.splice(atIndex, 1);
                                    setAlertTresholdConditions(newConditions);
                                    let newValues = clone(alertThresholdValues);
                                    newValues.splice(atIndex, 1);
                                    setAlertThresholdValues(newValues);
                                    let newSeverities = clone(alertThresholdSeverities);
                                    newSeverities.splice(atIndex, 1);
                                    setAlertThresholdSeverities(newSeverities);
                                  }}
                                >
                                  <DeleteForeverIcon sx={{ color: palette.primary }} />
                                </IconButton>
                              </Grid>
                            </Grid>
                          );
                        })}
                    </>
                  )}
                </FormControl>
              </div>
              <Divider style={{ marginTop: "1rem" }} />
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            style={{ backgroundColor: palette.secondary, color: "white" }}
            onClick={() => handleClose(setOpen)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          {
            <Button
              variant="contained"
              style={{
                backgroundColor: disabled ? palette.light : palette.primary,
              }}
              disabled={disabled}
              id={"save_data_button"}
              onClick={async () => {
                const thresholds = [];
                for (let index = 0; index < alertThresholdTypes.length; index++) {
                  const newTH = {
                    type: alertThresholdTypes[index],
                    condition: alertTresholdConditions[index],
                    value: alertThresholdValues[index],
                  };
                  thresholds.push(newTH);
                }
                const { values, ...cleanExtraInfo } = alertExtraInfo || {};

                const finalData = {
                  name: alertName,
                  desc: null,
                  type: alertType,
                  category: alertCategory,
                  subtype: alertSubType,
                  active: alertActive,
                  severity: alertSeverity,
                  thresholds: alertSeverityDefiner === "threshold" ? thresholds : [],
                  extraInfo: cleanExtraInfo,
                  values: alertSeverityDefiner === "values" ? alertSeverityValues : [],
                };
                const response = await postAlertConfiguration(finalData, intl);
                if (response?.status === 200) {
                  dispatch(
                    setConfigDataAction({
                      ...configData,
                      alertConfiguration: [...configData.alertConfiguration, finalData],
                    }),
                  );
                  handleClose(setOpen);
                }
              }}
            >
              {intl.formatMessage({ id: "accept" })}
            </Button>
          }
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AlertConfig;
